import { coordinate, positionKey, validateGameState, type GameState, type Move } from "../game";
import {
  getDifficultyPolicy,
  isBotDifficulty,
  type BotDifficulty,
  type SearchBudget,
} from "./difficulty";

export const BOT_PROTOCOL_VERSION = 2 as const;
const POSITION_KEY_LENGTH = 26;
const MAX_POSITION_HISTORY = 10_001;

function isPositionKey(value: unknown): value is string {
  if (typeof value !== "string" || value.length !== POSITION_KEY_LENGTH) return false;
  for (let index = 0; index < POSITION_KEY_LENGTH - 1; index += 1) {
    if (value.charCodeAt(index) > 15) return false;
  }
  return value.charCodeAt(POSITION_KEY_LENGTH - 1) <= 1;
}

export type BotRequestIdentity = Readonly<{
  matchId: string;
  revision: number;
}>;

export type BotSearchRequest = BotRequestIdentity &
  Readonly<{
    type: "search";
    protocolVersion: typeof BOT_PROTOCOL_VERSION;
    state: GameState;
    positionKeys: readonly string[];
    difficulty: BotDifficulty;
    seed: number;
    budget: SearchBudget;
  }>;

export type BotPingRequest = Readonly<{
  type: "ping";
  protocolVersion: typeof BOT_PROTOCOL_VERSION;
  nonce: string;
}>;

export type BotWorkerRequest = BotSearchRequest | BotPingRequest;

export type BotSearchResponse = BotRequestIdentity &
  Readonly<{
    type: "result";
    protocolVersion: typeof BOT_PROTOCOL_VERSION;
    move: Move;
    nodes: number;
    completedDepth: number;
    exhaustedBudget: boolean;
  }>;

export type BotErrorCode = "invalid-request" | "no-legal-move" | "search-failed";

export type BotErrorResponse = BotRequestIdentity &
  Readonly<{
    type: "error";
    protocolVersion: typeof BOT_PROTOCOL_VERSION;
    code: BotErrorCode;
  }>;

export type BotWorkerResponse = BotSearchResponse | BotErrorResponse;

export type BotPongResponse = Readonly<{
  type: "pong";
  protocolVersion: typeof BOT_PROTOCOL_VERSION;
  nonce: string;
}>;

export type BotWorkerMessageResponse = BotWorkerResponse | BotPongResponse;

export type ParseResult<T> =
  Readonly<{ ok: true; value: T }> | Readonly<{ ok: false; message: string }>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactlyKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(record).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function isIdentity(record: Record<string, unknown>): boolean {
  return (
    typeof record["matchId"] === "string" &&
    record["matchId"].length > 0 &&
    record["matchId"].length <= 128 &&
    Number.isSafeInteger(record["revision"]) &&
    Number(record["revision"]) >= 0
  );
}

function isBudget(value: unknown): value is SearchBudget {
  if (
    !isRecord(value) ||
    !hasExactlyKeys(value, [
      "maxDepth",
      "nodeLimit",
      "tableCapacity",
      "candidateCount",
      "plausibilityWindow",
    ])
  ) {
    return false;
  }

  const maxDepth = value["maxDepth"];
  const nodeLimit = value["nodeLimit"];
  const tableCapacity = value["tableCapacity"];
  const candidateCount = value["candidateCount"];
  const plausibilityWindow = value["plausibilityWindow"];
  return (
    Number.isSafeInteger(maxDepth) &&
    Number(maxDepth) >= 1 &&
    Number(maxDepth) <= 12 &&
    Number.isSafeInteger(nodeLimit) &&
    Number(nodeLimit) >= 1 &&
    Number(nodeLimit) <= 1_000_000 &&
    Number.isSafeInteger(tableCapacity) &&
    Number(tableCapacity) >= 1 &&
    Number(tableCapacity) <= 100_000 &&
    Number.isSafeInteger(candidateCount) &&
    Number(candidateCount) >= 1 &&
    Number(candidateCount) <= 64 &&
    Number.isSafeInteger(plausibilityWindow) &&
    Number(plausibilityWindow) >= 0 &&
    Number(plausibilityWindow) <= 1_000_000
  );
}

function isDifficultyBudget(difficulty: BotDifficulty, budget: SearchBudget): boolean {
  const expected = getDifficultyPolicy(difficulty).budget;
  return (
    budget.maxDepth === expected.maxDepth &&
    budget.nodeLimit === expected.nodeLimit &&
    budget.tableCapacity === expected.tableCapacity &&
    budget.candidateCount === expected.candidateCount &&
    budget.plausibilityWindow === expected.plausibilityWindow
  );
}

function parseMove(value: unknown): Move | null {
  if (!isRecord(value) || !hasExactlyKeys(value, ["kind", "from", "to"])) return null;
  if (value["kind"] !== "normal" && value["kind"] !== "passage") return null;
  if (!isRecord(value["from"]) || !hasExactlyKeys(value["from"], ["row", "column"])) return null;
  if (!isRecord(value["to"]) || !hasExactlyKeys(value["to"], ["row", "column"])) return null;

  const fromRow = value["from"]["row"];
  const fromColumn = value["from"]["column"];
  const toRow = value["to"]["row"];
  const toColumn = value["to"]["column"];
  if (
    !Number.isSafeInteger(fromRow) ||
    !Number.isSafeInteger(fromColumn) ||
    !Number.isSafeInteger(toRow) ||
    !Number.isSafeInteger(toColumn)
  ) {
    return null;
  }
  return Object.freeze({
    kind: value["kind"],
    from: coordinate(Number(fromRow), Number(fromColumn)),
    to: coordinate(Number(toRow), Number(toColumn)),
  });
}

function invalid<T>(message: string): ParseResult<T> {
  return Object.freeze({ ok: false, message });
}

function isNonce(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 128;
}

export function parseBotPingRequest(value: unknown): ParseResult<BotPingRequest> {
  if (
    !isRecord(value) ||
    !hasExactlyKeys(value, ["type", "protocolVersion", "nonce"]) ||
    value["type"] !== "ping" ||
    value["protocolVersion"] !== BOT_PROTOCOL_VERSION ||
    !isNonce(value["nonce"])
  ) {
    return invalid("Bot worker ping is malformed or unsupported.");
  }

  return Object.freeze({
    ok: true,
    value: Object.freeze({
      type: "ping",
      protocolVersion: BOT_PROTOCOL_VERSION,
      nonce: value["nonce"],
    }),
  });
}

export function parseBotPongResponse(value: unknown): ParseResult<BotPongResponse> {
  if (
    !isRecord(value) ||
    !hasExactlyKeys(value, ["type", "protocolVersion", "nonce"]) ||
    value["type"] !== "pong" ||
    value["protocolVersion"] !== BOT_PROTOCOL_VERSION ||
    !isNonce(value["nonce"])
  ) {
    return invalid("Bot worker pong is malformed or unsupported.");
  }

  return Object.freeze({
    ok: true,
    value: Object.freeze({
      type: "pong",
      protocolVersion: BOT_PROTOCOL_VERSION,
      nonce: value["nonce"],
    }),
  });
}

export function parseBotSearchRequest(value: unknown): ParseResult<BotSearchRequest> {
  if (
    !isRecord(value) ||
    !hasExactlyKeys(value, [
      "type",
      "protocolVersion",
      "matchId",
      "revision",
      "state",
      "positionKeys",
      "difficulty",
      "seed",
      "budget",
    ]) ||
    value["type"] !== "search" ||
    value["protocolVersion"] !== BOT_PROTOCOL_VERSION ||
    !isIdentity(value) ||
    !isBotDifficulty(value["difficulty"]) ||
    !Number.isSafeInteger(value["seed"]) ||
    Number(value["seed"]) < 0 ||
    Number(value["seed"]) > 0xffff_ffff ||
    !Array.isArray(value["positionKeys"]) ||
    value["positionKeys"].length === 0 ||
    value["positionKeys"].length > MAX_POSITION_HISTORY ||
    !value["positionKeys"].every(isPositionKey) ||
    !isBudget(value["budget"])
  ) {
    return invalid("Bot search request is malformed or unsupported.");
  }

  const state = validateGameState(value["state"]);
  if (!state.ok) return invalid("Bot search request contains an invalid game state.");
  if (value["positionKeys"].at(-1) !== positionKey(state.state)) {
    return invalid("Bot search request position history does not end at its game state.");
  }
  if (!isDifficultyBudget(value["difficulty"], value["budget"])) {
    return invalid("Bot search request budget does not match its difficulty policy.");
  }

  return Object.freeze({
    ok: true,
    value: Object.freeze({
      type: "search",
      protocolVersion: BOT_PROTOCOL_VERSION,
      matchId: value["matchId"] as string,
      revision: Number(value["revision"]),
      state: state.state,
      positionKeys: Object.freeze([...value["positionKeys"]]),
      difficulty: value["difficulty"],
      seed: Number(value["seed"]),
      budget: Object.freeze({ ...value["budget"] }),
    }),
  });
}

export function parseBotWorkerResponse(value: unknown): ParseResult<BotWorkerResponse> {
  if (!isRecord(value) || !isIdentity(value) || value["protocolVersion"] !== BOT_PROTOCOL_VERSION) {
    return invalid("Bot worker response identity is malformed or unsupported.");
  }

  if (value["type"] === "error") {
    if (
      !hasExactlyKeys(value, ["type", "protocolVersion", "matchId", "revision", "code"]) ||
      (value["code"] !== "invalid-request" &&
        value["code"] !== "no-legal-move" &&
        value["code"] !== "search-failed")
    ) {
      return invalid("Bot worker error response is malformed.");
    }
    return Object.freeze({
      ok: true,
      value: Object.freeze({
        type: "error",
        protocolVersion: BOT_PROTOCOL_VERSION,
        matchId: value["matchId"] as string,
        revision: Number(value["revision"]),
        code: value["code"],
      }),
    });
  }

  if (
    value["type"] !== "result" ||
    !hasExactlyKeys(value, [
      "type",
      "protocolVersion",
      "matchId",
      "revision",
      "move",
      "nodes",
      "completedDepth",
      "exhaustedBudget",
    ]) ||
    !Number.isSafeInteger(value["nodes"]) ||
    Number(value["nodes"]) < 0 ||
    Number(value["nodes"]) > 1_000_000 ||
    !Number.isSafeInteger(value["completedDepth"]) ||
    Number(value["completedDepth"]) < 0 ||
    Number(value["completedDepth"]) > 12 ||
    typeof value["exhaustedBudget"] !== "boolean"
  ) {
    return invalid("Bot worker result response is malformed.");
  }
  const move = parseMove(value["move"]);
  if (move === null) return invalid("Bot worker result move is malformed.");

  return Object.freeze({
    ok: true,
    value: Object.freeze({
      type: "result",
      protocolVersion: BOT_PROTOCOL_VERSION,
      matchId: value["matchId"] as string,
      revision: Number(value["revision"]),
      move,
      nodes: Number(value["nodes"]),
      completedDepth: Number(value["completedDepth"]),
      exhaustedBudget: value["exhaustedBudget"],
    }),
  });
}

export function extractRequestIdentity(value: unknown): BotRequestIdentity | null {
  if (!isRecord(value) || !isIdentity(value)) return null;
  return Object.freeze({
    matchId: value["matchId"] as string,
    revision: Number(value["revision"]),
  });
}
